import { FC, MutableRefObject, PropsWithChildren, ReactNode } from 'react'
import styled from 'styled-components'

import { useMediaQueryContext } from 'contexts/MediaQueryContext'
import { media } from 'styles/media'

import Button from '../display/Button'
import ButtonLink from '../display/ButtonLink'
import { SectionTitle } from '../display/Typography'

export const ChildrenWrapper = styled.div<{ mobileReverse: boolean }>`
  order: ${({ mobileReverse }) => mobileReverse && -1};
  position: relative;
  height: 66vw;
  width: 100%;

  ${media.xl`
    order: 2;
    height: 100%;
    width: 55%;
    flex-grow: 1;
  `}
`

export const Content = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  z-index: 1;
  padding: 16px;

  ${media.md`
    padding: 80px 80px 40px 80px;
  `}

  ${media.xl`
    padding: 100px;
    width: 45%;
  `}

  & > * {
    max-width: 720px;
  }

  & > * + * {
    margin-bottom: 20px;
  }

  h1 {
    color: ${({ theme }) => theme?.['-ds-core-400']?.value};
    font-size: 30px;
    font-weight: 400;
    line-height: 1.3;

    ${media.md`
      font-size: 64px;
      font-weight: 500;
      line-height: 1.2;
    `};
  }

  h2 {
    font-size: 16px;
    font-weight: 300;
    color: ${({ theme }) => theme?.['-ds-neutral-700']?.value};
    line-height: 1.4;

    ${media.md`
      font-size: 24px;
      font-weight: 300;
   `}
  }

  ${Button}, ${ButtonLink}, a {
    margin-top: 50px;

    ${media.md`
      width: fit-content;
    `}
  }
`

const HeaderInner = styled.div<{ backgroundColour?: string }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: stretch;
  position: relative;
  width: 100%;
  max-width: 1920px;
  margin: 0 auto;
  background-color: ${({ theme, backgroundColour }) =>
    backgroundColour || theme?.['-ds-core-100'].value};

  ${media.xl`
    /* offset height by header size */
    height: calc(100vh - 25vh);
    flex-direction: row;
  `}

  ${media.md`
    min-height: 630px;
  `}
`

export const useLearnMoreClickHandler = ({
  smallScreenTarget,
  largeScreenTarget,
}: {
  smallScreenTarget: MutableRefObject<HTMLElement>
  largeScreenTarget: MutableRefObject<HTMLElement>
}) => {
  const { xl: largerThanXl } = useMediaQueryContext()
  const smallerThanXl = !largerThanXl
  const handleLearnMoreClick = () => {
    if (smallerThanXl) {
      smallScreenTarget.current?.scrollIntoView({
        behavior: 'smooth',
      })
    } else {
      largeScreenTarget.current?.scrollIntoView({
        behavior: 'smooth',
      })
    }
  }

  return handleLearnMoreClick
}

type HeaderProps = {
  h1Text?: ReactNode
  h2Text: ReactNode
  h3Text: ReactNode
  onCtaClick?: () => any
  ctaInner?: ReactNode
  cta?: ReactNode
  backgroundColour?: string
}

const Header: FC<PropsWithChildren<HeaderProps>> = ({
  h1Text,
  h2Text,
  h3Text,
  onCtaClick,
  ctaInner,
  cta,
  children,
  backgroundColour,
}) => {
  const mobileImageTop = !onCtaClick && !cta

  return (
    <HeaderInner backgroundColour={backgroundColour}>
      <Content>
        {h1Text && <SectionTitle>{h1Text}</SectionTitle>}
        <h1>{h2Text}</h1>
        <h2>{h3Text}</h2>
        {cta ||
          (onCtaClick && (
            <Button onClick={onCtaClick} rounded>
              {ctaInner || 'Learn More'}
            </Button>
          ))}
      </Content>

      <ChildrenWrapper mobileReverse={mobileImageTop}>
        {children}
      </ChildrenWrapper>
    </HeaderInner>
  )
}

export default Header
